import { z } from 'zod';
import { platformStorageUploadTransportSchema } from '../storage/platform-storage.contract.js';
export const platformBrandAssetIdSchema = z.string().uuid();
export const platformBrandAssetVersionSchema = z.number().int().positive();
export const platformBrandAssetRoleSchema = z.enum([
    'light-logo',
    'dark-logo',
    'print-logo',
    'email-logo',
    'portal-logo',
    'document-logo',
    'favicon',
]);
export const platformBrandAssetStateSchema = z.enum([
    'pending-upload',
    'verified',
    'quarantined',
    'disabled',
    'revoked',
    'unavailable',
]);
export const platformBrandAssetContentTypeSchema = z.enum([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/x-icon',
]);
export const platformBrandAssetReferenceSchema = z.strictObject({
    brandAssetId: platformBrandAssetIdSchema,
    version: platformBrandAssetVersionSchema,
});
export const platformBrandAssetSchema = z.strictObject({
    id: platformBrandAssetIdSchema,
    version: platformBrandAssetVersionSchema,
    role: platformBrandAssetRoleSchema,
    displayName: z.string().trim().min(1).max(120),
    originalFileName: z.string().trim().min(1).max(240),
    contentType: platformBrandAssetContentTypeSchema,
    sizeBytes: z.number().int().positive().max(6 * 1_024 * 1_024),
    state: platformBrandAssetStateSchema,
    verifiedAt: z.string().datetime({ offset: true }).nullable(),
    disabledAt: z.string().datetime({ offset: true }).nullable(),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const platformBrandAssetUploadRequestSchema = z.strictObject({
    brandAssetId: platformBrandAssetIdSchema.nullable().default(null),
    role: platformBrandAssetRoleSchema,
    displayName: z.string().trim().min(1).max(120),
    idempotencyKey: z.string().uuid(),
    originalFileName: z.string().trim().min(1).max(240),
    contentType: platformBrandAssetContentTypeSchema,
    sizeBytes: z.number().int().positive().max(6 * 1_024 * 1_024),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export const platformBrandAssetUploadAuthorizationResponseSchema = z.strictObject({
    data: z.strictObject({
        asset: platformBrandAssetSchema,
        uploadIntentId: z.string().uuid(),
        transport: platformStorageUploadTransportSchema,
    }),
});
export const platformBrandAssetFinalizeRequestSchema = z.strictObject({
    uploadIntentId: z.string().uuid(),
});
export const platformBrandAssetResponseSchema = z.strictObject({ data: platformBrandAssetSchema });
export const platformBrandAssetListResponseSchema = z.strictObject({
    data: z.array(platformBrandAssetSchema),
});
export const platformApprovedBrandAssetSchema = z.strictObject({
    reference: platformBrandAssetReferenceSchema,
    role: platformBrandAssetRoleSchema,
    url: z.string().url().superRefine((value, context) => {
        const url = new URL(value);
        const loopback = ['127.0.0.1', 'localhost', '::1'].includes(url.hostname);
        if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) {
            context.addIssue({
                code: 'custom',
                message: 'Approved brand asset URLs must use HTTPS outside loopback development.',
            });
        }
        if (url.username !== '' || url.password !== '' || url.hash !== '') {
            context.addIssue({
                code: 'custom',
                message: 'Approved brand asset URLs cannot contain credentials or fragments.',
            });
        }
    }),
    expiresAt: z.string().datetime({ offset: true }),
});
export const platformApprovedBrandAssetsSchema = z.partialRecord(platformBrandAssetRoleSchema, platformApprovedBrandAssetSchema);
//# sourceMappingURL=platform-brand-asset.contract.js.map